import React, { useState, useEffect } from 'react';
import { Box, Paper, Container, Snackbar, Alert, Button, } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import EventFormStepper from './EventFormStepper';
import BasicInfoForm from './BasicInfoForm';
import PaymentForm from './PaymentForm';
import TicketsStep from './payment/TicketsStep';
import RegistrationForm from './RegistrationForm';
// import StoreForm from './StoreForm';
import EventPageForm from './EventPageForm';
import {
  buildEventFormValues,
  EVENT_FORM_DEFAULT_VALUES,
  eventFormFullSchema,
  finalizeEventFormDataForSubmit,
  getFirstEventFormError,
} from './eventFormShared';
import type { EventFormData } from './eventFormTypes';

export type { EventFormData } from './eventFormTypes';

interface EventFormContainerProps {
  initialData?: Partial<EventFormData>;
  initialStep?: number;
  onSubmit: (data: EventFormData) => void;
  onSaveDraft?: (data: EventFormData) => void;
  onReloadEvent?: () => void;
}

const EventFormContainer: React.FC<EventFormContainerProps> = ({ 
  initialData,
  initialStep = 0,
  onSubmit,
  onSaveDraft,
  onReloadEvent,
}) => {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });
  const [autoSave, setAutoSave] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [slug, setSlug] = useState('');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (initialStep >= 0 && initialStep <= 3) {
      setActiveStep(initialStep);
    }
  }, [initialStep, initialData?._id]);

  const methods = useForm<EventFormData>({
  defaultValues: buildEventFormValues(initialData),
  resolver: yupResolver(eventFormFullSchema) as any,
  mode: 'onChange',
  shouldUnregister: false,
  context: {
    isPaid: initialData?.isPaid ?? EVENT_FORM_DEFAULT_VALUES.isPaid,
  },
});

  useEffect(() => {
    if (initialData?._id) {
      methods.reset(buildEventFormValues(initialData));
    }
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Atualizar isPaid automaticamente baseado nos ingressos
  const tickets = methods.watch('tickets');
  const ticketCatalogMode = methods.watch('ticketCatalogMode');
  const ticketTypes = methods.watch('ticketTypes');
  const salesPhases = methods.watch('salesPhases');
  useEffect(() => {
    let hasPaidTicket = false;
    if (ticketCatalogMode === 'phased') {
      hasPaidTicket = (salesPhases || []).some((phase) =>
        (phase.offers || []).some((offer) => (offer.price ?? 0) > 0)
      );
    } else if (ticketCatalogMode === 'batched') {
      hasPaidTicket = (ticketTypes || []).some((type) =>
        (type.batches || []).some((batch) => (batch.price ?? 0) > 0)
      );
    } else if (tickets && Array.isArray(tickets)) {
      hasPaidTicket = tickets.some((ticket: { price?: number }) => (ticket?.price ?? 0) > 0);
    }
    methods.setValue('isPaid', hasPaidTicket, { shouldValidate: false });
  }, [tickets, ticketCatalogMode, ticketTypes, salesPhases, methods]);

  console.log('[EventFormContainer] inicializado', {
    initialData,
    defaultValues: EVENT_FORM_DEFAULT_VALUES,
  });

  // Auto-save a cada 30 segundos
  // useEffect(() => {
  //   console.log('[EventFormContainer] etapa:', activeStep);
  //   if (!autoSave || !onSaveDraft) return;
    
  //   const timer = setInterval(() => {
  //     const currentData = methods.getValues();
  //     onSaveDraft(currentData as EventFormData);
  //     setSnackbar({
  //       open: true,
  //       message: 'Rascunho salvo automaticamente',
  //       severity: 'info'
  //     });
  //   }, 30000);
    
  //   return () => clearInterval(timer);
  // }, [autoSave, methods, onSaveDraft]);

  // Manipuladores de navegação entre etapas
  const handleNext = async () => {
    // Validar apenas os campos da etapa atual antes de avançar
    let isValid = false;
    
    switch (activeStep) {
      case 0:
        isValid = await methods.trigger([
          'title', 'slug', 'description', 'startDate', 'endDate', 
          'location', 'address', 'city', 'state', 'category', 'capacity'
        ]);
        break;
      case 1: {
        const mode = methods.getValues('ticketCatalogMode') || 'legacy';
        if (mode === 'phased') {
          isValid = await methods.trigger(['ticketTypes', 'salesPhases']);
        } else if (mode === 'batched') {
          isValid = await methods.trigger(['ticketTypes']);
        } else {
          isValid = await methods.trigger(['isPaid', 'tickets']);
        }
        break;
      }
      case 2:
        isValid = await methods.trigger(['registrationDeadline', 'customFields']);
        break;
      case 3:
        isValid = true; // A última etapa não tem validações obrigatórias
        break;
      default:
        isValid = false;
    }
    
    if (isValid) {
      setActiveStep((prevStep) => (prevStep < 3 ? prevStep + 1 : prevStep));
    } else {
      setSnackbar({
        open: true,
        message: 'Por favor, corrija os erros antes de continuar',
        severity: 'error'
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSaveDraft = () => {
    if (onSaveDraft) {
      const currentData = methods.getValues();
      console.log('[EventFormContainer] autoSave paused:', currentData);

      onSaveDraft({...currentData, status: 'paused'} as EventFormData);
      setSnackbar({
        open: true,
        message: 'Rascunho salvo com sucesso',
        severity: 'success'
      });
    }
  };

  const handleSubmit = methods.handleSubmit(
    (data) => {
      console.log('[EventFormContainer] handleSubmit acionado', data);
      onSubmit(finalizeEventFormDataForSubmit(data as EventFormData));
    },
    (errors) => {
      console.error('[EventFormContainer] erros de validação ao publicar:', errors);
      const firstError = getFirstEventFormError(errors);
      setSnackbar({
        open: true,
        message: firstError
          ? `Não foi possível publicar: ${firstError}`
          : 'Corrija os erros do formulário antes de publicar. Revise todas as abas.',
        severity: 'error',
      });
    }
  );

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const togglePreviewMode = () => {
    setPreviewMode(!previewMode);
  };

  // Renderização do conteúdo de cada etapa
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <BasicInfoForm 
          slugError={slugError}
          setSlugError={setSlugError}
          checking={checking}
          setChecking={setChecking}
        />;
      case 1:
        return (
          <TicketsStep
            eventId={methods.watch('_id')}
            onReload={onReloadEvent}
          />
        );
      case 2:
        return <RegistrationForm />;
      case 3:
        return <EventPageForm previewMode={previewMode} togglePreviewMode={togglePreviewMode} />;
      default:
        return 'Etapa desconhecida';
    }
  };


  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <FormProvider {...methods}>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
          <EventFormStepper activeStep={activeStep} />
          
          <Box sx={{ mt: 4, mb: 2 }}>
            {getStepContent(activeStep)}
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            mt: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ mr: 1 }}
              >
                Voltar
              </Button>
              
              {onSaveDraft && (
                <Button
                  variant="outlined"
                  onClick={handleSaveDraft}
                >
                  Salvar Rascunho
                </Button>
              )}
            </Box>
            
            <Box>
              {activeStep === 3 ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={methods.formState.isSubmitting}
                >
                  Publicar Evento
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  Próximo
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      </FormProvider>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EventFormContainer;
